const ARTICLE_PUBLISHED_AT = {
  'how-to-practice-shadowing-with-audio': '2026-07-09',
  'best-way-to-turn-podcasts-into-speaking-practice': '2026-07-09',
  'audio-to-text-for-language-learning': '2026-07-09',
  'text-to-speech-for-ielts-speaking': '2026-07-09',
  'how-to-use-mp3-subtitles-for-listening-practice': '2026-07-09',
  'why-speak-english-out-loud-every-day': '2026-07-17',
  'bilingual-subtitles-listening-speaking': '2026-07-17',
  'intensive-listening-with-ai-transcripts': '2026-07-17',
  'why-cant-i-understand-spoken-english': '2026-07-17',
  'how-to-review-ielts-listening-mistakes': '2026-07-17',
  'how-to-practice-english-job-interview-speaking': '2026-07-17',
  'how-to-review-phrases-from-listening-materials': '2026-07-17',
  'how-to-shadow-bbc-6-minute-english': '2026-10-07'
}

const EVIDENCE_SOURCES = {
  shadowing: {
    title: 'Effects of Video-based Shadowing on Suprasegmental Features',
    publisher: 'rEFLections, 2024',
    url: 'https://doi.org/10.61508/refl.v31i3.275498',
    note: 'A study of pre-intermediate EFL learners that reported improvement in suprasegmental pronunciation, especially intonation. Results are specific to its participants and training design.'
  },
  captions: {
    title: 'Listen or Read? Learners’ Reliance on Captions',
    publisher: 'Behavioral Sciences, 2025',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12024247/',
    note: 'A study of 64 Chinese EFL learners showing that caption reliance differed by proficiency and video complexity. It supports using captions selectively rather than reading every line by default.'
  },
  asr: {
    title: 'The impact of automatic speech recognition on L2 pronunciation and speaking',
    publisher: 'Frontiers in Psychology, 2023',
    url: 'https://doi.org/10.3389/fpsyg.2023.1210187',
    note: 'A 14-week study with 61 intermediate Chinese EFL learners. The intervention combined ASR with peer correction, so its results should not be attributed to automated feedback alone.'
  },
  ielts: {
    title: 'IELTS Speaking Band Descriptors',
    publisher: 'IELTS',
    url: 'https://ielts.org/cdn/ielts-guides/ielts-speaking-band-descriptors.pdf',
    note: 'The official public descriptors define IELTS speaking criteria. GoodScriber practice feedback is not an official IELTS score or score prediction.'
  }
}

const ARTICLE_EVIDENCE = {
  'how-to-practice-shadowing-with-audio': ['shadowing'],
  'best-way-to-turn-podcasts-into-speaking-practice': ['shadowing', 'captions'],
  'audio-to-text-for-language-learning': ['captions'],
  'text-to-speech-for-ielts-speaking': ['ielts'],
  'how-to-use-mp3-subtitles-for-listening-practice': ['captions'],
  'why-speak-english-out-loud-every-day': ['asr'],
  'bilingual-subtitles-listening-speaking': ['captions'],
  'intensive-listening-with-ai-transcripts': ['captions'],
  'why-cant-i-understand-spoken-english': ['captions'],
  'how-to-practice-english-job-interview-speaking': ['asr'],
  'how-to-shadow-bbc-6-minute-english': ['shadowing']
}

const learnArticleDrafts = [
  {
    slug: 'how-to-practice-shadowing-with-audio',
    title: 'How do I practice shadowing with any audio file?',
    description: 'Learn how to turn any audio file into sentence-by-sentence shadowing practice with subtitles, replay, and speaking repetition.',
    tldr: 'Upload your audio, generate a transcript or subtitle track, split it into short sentences, then listen and repeat one sentence at a time. Shadowing works best when you can replay, pause, compare, and repeat without losing your place.',
    sections: [
      {
        heading: 'Why this works',
        body: [
          'Shadowing trains listening, rhythm, pronunciation, and speaking confidence at the same time. Raw audio is hard to control when you need to repeat one sentence many times.',
          'GoodScriber turns the audio into subtitle-based practice material, so you can focus on one line, replay it, speak after it, and move through the file at a steady pace.'
        ]
      },
      {
        heading: 'Step-by-step workflow',
        list: [
          'Upload an MP3, MP4, podcast clip, lesson recording, or speaking sample.',
          'Generate a transcript or subtitle track.',
          'Review the sentence split and edit any line that needs cleanup.',
          'Play one sentence, repeat it aloud, then replay until the rhythm feels natural.',
          'Use translation or word lookup only when meaning blocks your speaking flow.'
        ]
      },
      {
        heading: 'What to prepare',
        table: {
          headers: ['Input', 'Best for', 'GoodScriber feature'],
          rows: [
            ['MP3 podcast', 'Natural listening and pronunciation', 'Audio to subtitles'],
            ['Speaking script', 'IELTS, TOEFL, interview answers', 'Text to speech'],
            ['Video lesson', 'Real expression and rhythm', 'Video to subtitles']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner uploads a three-minute podcast clip. GoodScriber creates subtitles, highlights the current sentence, and lets the learner repeat the same line until the stress and pauses feel closer to the original speaker.'
        ]
      }
    ],
    faqs: [
      ['How long should one shadowing session be?', 'Ten to fifteen focused minutes is enough for most learners. Short sessions make it easier to repeat carefully instead of rushing through the audio.'],
      ['Should I read the transcript while shadowing?', 'Use the transcript when you are learning the line, then try to repeat with less visual support once the sound pattern is familiar.'],
      ['Can I use podcasts for shadowing?', 'Yes. Podcasts are strong practice material because they contain natural speed, pauses, fillers, and real sentence rhythm.'],
      ['Do I need word-level subtitles?', 'Word-level subtitles help when you need tight timing or want to notice exactly where a word starts and ends. Sentence subtitles are often enough for fluency practice.'],
      ['What should I do after generating subtitles?', 'Pick a short section, replay one sentence, repeat it aloud, and keep practicing until the sentence feels smooth.']
    ]
  },
  {
    slug: 'best-way-to-turn-podcasts-into-speaking-practice',
    title: 'What is the best way to turn podcasts into speaking practice?',
    description: 'A practical workflow for converting podcasts into subtitle-based listening and speaking practice with repeatable sentence drills.',
    tldr: 'The best way to use podcasts for speaking practice is to convert a short clip into subtitles, choose useful sentences, and shadow them repeatedly. Podcasts work well because they contain natural phrasing, real pauses, and everyday vocabulary.',
    sections: [
      {
        heading: 'Why podcasts are useful',
        body: [
          'Podcasts expose you to natural speed and real phrasing. They become stronger speaking material when the audio is broken into lines you can replay and repeat.'
        ]
      },
      {
        heading: 'Podcast practice workflow',
        list: [
          'Choose a short clip instead of a full episode.',
          'Generate subtitles from the audio.',
          'Mark five to ten useful sentences.',
          'Listen once for meaning, then replay one sentence at a time.',
          'Repeat the line aloud and copy the speaker rhythm.'
        ]
      },
      {
        heading: 'Podcast clip types',
        table: {
          headers: ['Clip type', 'Practice goal', 'Recommended mode'],
          rows: [
            ['Interview answer', 'Natural response structure', 'Sentence shadowing'],
            ['Story section', 'Intonation and pacing', 'Replay and repeat'],
            ['Explanation clip', 'Academic vocabulary', 'Subtitle study']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner studying business English uploads a podcast interview clip, generates subtitles, and repeats the guest answer line by line until the transitions sound more natural.'
        ]
      }
    ],
    faqs: [
      ['Should I use a whole podcast episode?', 'Start with one to three minutes. Full episodes are useful for listening, but short clips are better for speaking drills.'],
      ['What kind of podcast is best?', 'Choose podcasts with clear speech, topics you care about, and sentence patterns you would actually use.'],
      ['Can subtitles hurt listening practice?', 'Subtitles help when used intentionally. Listen first, check the subtitle, then repeat without staring at every word.'],
      ['How many sentences should I practice?', 'Five strong sentences are better than twenty rushed lines.'],
      ['Can GoodScriber translate podcast subtitles?', 'Yes. Subtitle translation can help you understand the line before you practice speaking it.']
    ]
  },
  {
    slug: 'audio-to-text-for-language-learning',
    title: 'Why is audio-to-text useful for language learning?',
    description: 'Audio-to-text helps language learners turn listening material into searchable, repeatable, and subtitle-based study content.',
    tldr: 'Audio-to-text is useful because it turns spoken language into material you can read, search, replay, translate, and practice. For language learners, a transcript makes listening less passive and helps connect sound, meaning, vocabulary, and pronunciation.',
    sections: [
      {
        heading: 'What audio-to-text gives learners',
        body: [
          'A transcript makes spoken language visible. Once the audio is converted to text, you can find difficult words, replay the exact sentence, and practice speaking with the original rhythm.'
        ]
      },
      {
        heading: 'How to use transcripts for study',
        list: [
          'Listen once without reading.',
          'Read the transcript and mark unclear phrases.',
          'Replay only the difficult sentences.',
          'Shadow the lines aloud.',
          'Export or save useful phrases for review.'
        ]
      },
      {
        heading: 'Learning benefits',
        table: {
          headers: ['Problem', 'Transcript benefit', 'Practice action'],
          rows: [
            ['Fast speech', 'Shows sentence boundaries', 'Replay one line'],
            ['Unknown words', 'Makes vocabulary searchable', 'Look up and repeat'],
            ['Weak pronunciation', 'Connects sound to text', 'Shadow the sentence']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner uploads an English lesson recording, generates a transcript, finds the lines they missed while listening, and practices those exact sentences with subtitles and replay.'
        ]
      }
    ],
    faqs: [
      ['Is audio-to-text the same as subtitles?', 'A transcript is the text version of speech. Subtitles add timing so the text can follow the audio line by line.'],
      ['Does audio-to-text improve speaking?', 'It helps speaking when you use the transcript for shadowing, repetition, and phrase practice.'],
      ['Can I use audio-to-text for exam practice?', 'Yes. It is useful for IELTS, TOEFL, and other listening or speaking preparation.'],
      ['Should beginners use transcripts?', 'Yes, but they should still listen before reading so they do not become fully dependent on text.'],
      ['Can I export transcripts from GoodScriber?', 'GoodScriber supports exporting transcript and subtitle files for study or reuse.']
    ]
  },
  {
    slug: 'text-to-speech-for-ielts-speaking',
    title: 'How can text-to-speech help IELTS speaking practice?',
    description: 'Use text-to-speech to turn IELTS speaking answers into natural audio for repeat-after practice, pacing, and pronunciation training.',
    tldr: 'Text-to-speech helps IELTS speaking practice by turning sample answers into audio you can listen to and repeat. This is useful when you want to practice pronunciation, pacing, sentence stress, and answer delivery before speaking on your own.',
    sections: [
      {
        heading: 'Why TTS helps IELTS answers',
        body: [
          'Many learners write good IELTS answers but cannot deliver them naturally. Text-to-speech gives you a clean audio model, so you can hear the answer before practicing it aloud.'
        ]
      },
      {
        heading: 'IELTS speaking workflow',
        list: [
          'Write or paste a Part 1, Part 2, or Part 3 answer.',
          'Generate natural speech from the text.',
          'Listen for stress, pauses, and linking.',
          'Repeat one sentence at a time.',
          'Record yourself and compare your rhythm with the audio model.'
        ]
      },
      {
        heading: 'Where TTS fits',
        table: {
          headers: ['IELTS task', 'TTS use', 'Practice focus'],
          rows: [
            ['Part 1', 'Short answers', 'Speed and clarity'],
            ['Part 2', 'Long turn sample', 'Pacing and structure'],
            ['Part 3', 'Opinion answers', 'Transitions and stress']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner writes a Part 2 answer about a memorable place, generates speech in GoodScriber, and shadows the answer until the pauses and emphasis feel natural.'
        ]
      }
    ],
    faqs: [
      ['Can TTS replace real speaking practice?', 'TTS gives you a model, but you still need to speak aloud and build your own delivery.'],
      ['Is TTS useful for IELTS Part 2?', 'Yes. It helps you practice longer answers with better pacing and sentence stress.'],
      ['Should I memorize TTS answers?', 'Use TTS to learn rhythm and phrasing. Avoid memorizing full answers mechanically.'],
      ['Can I slow down the audio?', 'A slower voice can help beginners notice pronunciation and stress before practicing at a more natural speed.'],
      ['Can GoodScriber turn my script into practice audio?', 'Yes. You can paste text, generate speech, and use it for repeat-after practice.']
    ]
  },
  {
    slug: 'how-to-use-mp3-subtitles-for-listening-practice',
    title: 'How do I use MP3 subtitles for listening and speaking practice?',
    description: 'Turn MP3 audio into timed subtitles, then use sentence replay, bilingual checking, and shadowing to improve listening and speaking.',
    tldr: 'The useful goal is not just generating subtitles from an MP3. The stronger learning workflow is to create timed lines, find the sentences you missed, replay them, and shadow them aloud. MP3 subtitles make listening practice more precise because every difficult line becomes repeatable.',
    sections: [
      {
        heading: 'Why MP3 subtitles help learners',
        body: [
          'Many learners listen to a full audio file and only remember that it felt difficult. Timed subtitles show exactly which sentence was missed, so practice can move from vague listening to targeted repetition.',
          'GoodScriber can turn a podcast clip, lesson recording, or speaking sample into timed lines that are easier to replay, translate, and shadow.'
        ]
      },
      {
        heading: 'Learning-first workflow',
        list: [
          'Choose a short MP3 clip, ideally one to three minutes.',
          'Generate timed subtitles from the audio.',
          'Listen once without reading, then mark sentences you missed.',
          'Check the subtitle and translation only for those difficult lines.',
          'Replay each line and repeat it aloud until the rhythm feels natural.'
        ]
      },
      {
        heading: 'How to use each output',
        table: {
          headers: ['Output', 'Learning use', 'Practice action'],
          rows: [
            ['Timed subtitles', 'Locate missed sentences', 'Replay one line'],
            ['Plain transcript', 'Review vocabulary and phrases', 'Save useful expressions'],
            ['Bilingual subtitles', 'Check difficult meaning', 'Return to the original line and shadow']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner uploads a two-minute MP3 from an English podcast. After subtitles are generated, they find five sentences they could not catch, replay each line, read the subtitle, and then shadow the original audio until the sentence sounds familiar.'
        ]
      }
    ],
    faqs: [
      ['Can MP3 subtitles improve listening?', 'Yes, when you use them to locate missed sentences and replay those lines instead of reading the whole transcript passively.'],
      ['Should I translate every subtitle line?', 'Translate only the lines that block understanding. Too much translation can turn listening practice into reading practice.'],
      ['How long should the MP3 clip be?', 'One to three minutes is enough for focused practice. A short clip makes it easier to repeat and review carefully.'],
      ['Can I use MP3 subtitles for shadowing?', 'Yes. Timed subtitle lines make it easier to repeat one sentence at a time and copy the speaker rhythm.'],
      ['Do I need perfect subtitles for study?', 'Study subtitles do not need to be perfect, but you should correct important names, numbers, and sentences you plan to practice aloud.']
    ]
  },
  {
    slug: 'why-speak-english-out-loud-every-day',
    title: 'Why should you speak English out loud every day?',
    description: 'Daily speaking helps your tongue, lips, jaw, voice, and breathing learn the movement patterns of English pronunciation. Try this 10-minute routine.',
    tldr: 'Speaking English out loud every day helps turn unfamiliar pronunciation movements into more automatic motor patterns. Listening builds sound recognition, but your tongue, lips, jaw, voice, and breathing still need real practice. A short daily routine of listening, repeating, recording, and reviewing difficult sentences is more useful than silent study alone.',
    sections: [
      {
        heading: 'What does pronunciation muscle memory mean?',
        body: [
          '"Muscle memory" is a convenient name for procedural motor learning. Your muscles do not store English words by themselves. Your brain learns the sequence and timing of the movements used to produce a sound or sentence.',
          'Languages use the same speech organs, but they do not rely on exactly the same tongue positions, lip shapes, airflow patterns, stress, or rhythm. A movement that is uncommon in your first language may feel slow and deliberate when you begin practicing English.',
          'With accurate repetition, these movements require less conscious control. That leaves more attention available for meaning, vocabulary, and the conversation itself.'
        ]
      },
      {
        heading: 'Why listening and silent reading are not enough',
        body: [
          'Listening helps you recognize English sounds, but recognition and production are different skills. You can know how a word should sound and still struggle to pronounce it smoothly in a sentence.',
          'Silent reading does not make your tongue, lips, jaw, voice, and breathing complete the real movement. Speaking out loud connects the sound you hear with the movement needed to produce it.'
        ]
      },
      {
        heading: 'A 10-minute daily speaking routine',
        list: [
          'Choose 30 to 60 seconds of English audio that you mostly understand.',
          'Listen once, then check the transcript or subtitles for meaning.',
          'Practice one sentence at a time and notice the speaker\'s mouth movement, stress, and rhythm.',
          'Repeat each sentence three to five times at a normal speaking volume.',
          'Record the final repetition and compare it with the reference audio.',
          'The next day, review the two hardest sentences before starting new material.'
        ]
      },
      {
        heading: 'Pronunciation movements worth practicing',
        table: {
          headers: ['Practice target', 'Movement focus', 'Common difficulty'],
          rows: [
            ['The th sounds', 'Place the tongue lightly at or between the teeth and control the airflow', 'Replacing th with a more familiar s, z, d, or t sound'],
            ['English r and l', 'Separate the tongue positions and tongue shape', 'Hearing the difference but producing it inconsistently'],
            ['Final consonants', 'Complete the final consonant movement', 'Dropping the ending when speaking quickly'],
            ['Stress and weak forms', 'Emphasize key syllables and reduce less important words', 'Giving every word equal weight'],
            ['Linking and rhythm', 'Connect nearby words without losing important sounds', 'Pausing after every word']
          ]
        }
      },
      {
        heading: 'Example scenario',
        body: [
          'Suppose you want to practice "I really appreciate your help." Add the reference audio or text to GoodScriber and use the sentence-level audio and subtitles. Listen first, repeat the sentence five times at a normal volume, and record the final attempt. Focus on the r in really, the stress in appreciate, and whether the whole sentence stays connected. Review it again the next day until you can say it naturally without reading.'
        ]
      }
    ],
    faqs: [
      ['Does speaking English every day really help?', 'Yes. Regular speaking develops pronunciation movement, rhythm, and the connection between an idea and spoken output. Use a clear audio model so you do not repeatedly practice an avoidable error.'],
      ['How long should I speak English each day?', 'There is no universal minimum. Ten to twenty minutes of focused daily practice is often easier to sustain than one long session every few days.'],
      ['Can I build muscle memory by shadowing?', 'Yes. Shadowing can train timing, stress, linking, and pronunciation movement when you use a short recording and repeat it accurately.'],
      ['Do I have to speak loudly?', 'You do not need to shout. Use a normal speaking volume so your voice, airflow, tongue, and lips complete the real pronunciation movement.'],
      ['How can GoodScriber support daily speaking practice?', 'You can turn your own audio or text into sentence-level practice, replay difficult lines, read timed subtitles, and repeat the sentences aloud.']
    ]
  },
  {
    slug: 'bilingual-subtitles-listening-speaking',
    title: 'Do bilingual subtitles help listening and speaking?',
    description: 'Use bilingual subtitles to check difficult meaning, then return to English audio for focused listening, repetition, and speaking practice.',
    tldr: 'Bilingual subtitles help when they solve a specific comprehension problem and then get out of the way. Listen first, check the English line, use the translation only when meaning is still unclear, and return to the original audio for repetition. Reading both languages throughout the whole recording can reduce the listening work your brain needs to do.',
    sections: [
      {
        heading: 'When bilingual subtitles are useful',
        body: [
          'Bilingual subtitles are most useful when you recognize the English words but cannot understand the sentence quickly enough. A short translation check can reveal the intended meaning, an idiom, or the relationship between two ideas.',
          'They are less useful when your main problem is sound recognition. If you cannot hear where one word ends and the next begins, replaying the English line and comparing it with the English subtitle should come before translation.'
        ]
      },
      {
        heading: 'A three-pass subtitle workflow',
        list: [
          'Listen once without subtitles and identify the general topic.',
          'Listen again with English subtitles and mark only the lines you missed.',
          'Reveal the translation for a difficult line when the English text is still unclear.',
          'Hide the translation, replay the English audio, and repeat the sentence aloud.',
          'Finish by listening to the whole clip again without reading.'
        ]
      },
      {
        heading: 'Choose the right subtitle mode',
        table: {
          headers: ['Subtitle mode', 'Best use', 'Next action'],
          rows: [
            ['No subtitles', 'Test real listening comprehension', 'Write down what you heard'],
            ['English subtitles', 'Connect sounds with English words', 'Replay and shadow the line'],
            ['Bilingual subtitles', 'Resolve difficult meaning quickly', 'Hide the translation and return to English']
          ]
        }
      },
      {
        heading: 'Avoid turning listening into translation reading',
        body: [
          'Keeping the translation visible from beginning to end makes it easy to follow the story without processing the English audio closely. Use translation as a temporary support for selected lines.',
          'GoodScriber lets you work with timed subtitles and translation at sentence level, so you can check meaning without losing your place in the audio.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner studies a two-minute interview. They understand the topic but miss six lines. They open the English subtitles, solve four lines through replay, check the translation for the remaining two, and then shadow all six English sentences. On the final listen, they hide both subtitle tracks and check whether the phrases are now recognizable.'
        ]
      }
    ],
    faqs: [
      ['Should beginners always use bilingual subtitles?', 'Beginners can use them for difficult meaning, but should still include short periods of listening with English-only subtitles and no subtitles.'],
      ['Can bilingual subtitles improve speaking?', 'Yes, when you return to the English audio and repeat useful sentences aloud after checking the meaning.'],
      ['Should I translate every sentence?', 'No. Translate only the lines that block understanding so the session remains focused on English listening.'],
      ['When should I hide the translation?', 'Hide it as soon as you understand the line, then replay and repeat the original English sentence.'],
      ['How can GoodScriber help with bilingual subtitle practice?', 'You can generate timed subtitles, check translations for difficult lines, replay sentences, and use the English audio for shadowing.']
    ]
  },
  {
    slug: 'intensive-listening-with-ai-transcripts',
    title: 'How do I practice intensive listening with an English transcript?',
    description: 'Use a short audio clip and transcript to locate missed words, replay difficult sentences, check meaning, and build accurate listening habits.',
    tldr: 'Effective intensive listening uses a short recording and treats the transcript as a diagnostic tool. Listen before reading, write or recall what you heard, compare it with the transcript, identify why each line was missed, and replay only the difficult sentences. Finish with a clean listen so the sounds become recognizable without text.',
    sections: [
      {
        heading: 'What intensive listening trains',
        body: [
          'Intensive listening means studying a short piece of audio closely enough to notice exact words, reduced sounds, linking, stress, and meaning. It is different from listening to a long program for general exposure.',
          'A transcript makes hidden problems visible. It tells you whether you lacked vocabulary, knew the word but failed to recognize its spoken form, or lost the sentence because it was too long.'
        ]
      },
      {
        heading: 'A practical intensive listening routine',
        list: [
          'Choose 30 seconds to two minutes of audio that is slightly challenging.',
          'Listen once for the main idea without reading.',
          'Replay one sentence and write down or say what you heard.',
          'Compare your version with the transcript and mark the exact difference.',
          'Check unknown words, linking, weak forms, or sentence structure.',
          'Replay and shadow the difficult sentence three to five times.',
          'Listen to the full clip once more without the transcript.'
        ]
      },
      {
        heading: 'Diagnose each missed sentence',
        table: {
          headers: ['Problem', 'What you notice', 'Practice response'],
          rows: [
            ['Unknown word', 'The transcript contains unfamiliar vocabulary', 'Learn the meaning and hear it again in context'],
            ['Known word, missed sound', 'You know the word after reading it', 'Replay the phrase and copy its connected pronunciation'],
            ['Long sentence', 'You understand separate words but lose the idea', 'Divide it into meaning groups and retell each group'],
            ['Attention gap', 'You can hear it on the next attempt', 'Shorten the clip and listen with a clear task']
          ]
        }
      },
      {
        heading: 'Use the transcript after the first listen',
        body: [
          'Opening the transcript immediately removes the diagnostic step. Give your ears a real attempt first, then use the text to explain the gap between what was spoken and what you perceived.',
          'With GoodScriber, timed subtitle lines make it easier to replay the exact sentence instead of searching through the full recording.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner studies a 60-second podcast clip. They miss three sentences. The transcript shows one unknown phrase, one linked expression they already knew, and one long sentence with several clauses. They review each problem differently, shadow the three lines, and then listen to the full minute again without text.'
        ]
      }
    ],
    faqs: [
      ['How long should intensive listening audio be?', 'Thirty seconds to two minutes is usually enough for detailed work without making the session too long.'],
      ['Should I transcribe every word myself?', 'Partial dictation is useful, but you can focus on the sentences you missed instead of transcribing the entire clip every time.'],
      ['Can AI transcripts contain mistakes?', 'Yes. Check important names, numbers, and unclear phrases against the audio, especially when a line does not make sense.'],
      ['How often should I do intensive listening?', 'Three to five short sessions each week can work well when you also keep some relaxed extensive listening in your routine.'],
      ['How does GoodScriber support intensive listening?', 'It can turn audio into timed subtitle lines so you can compare text, replay difficult sentences, and practice them aloud.']
    ]
  },
  {
    slug: 'why-cant-i-understand-spoken-english',
    title: 'Why can I read English but not understand spoken English?',
    description: 'Find out whether vocabulary, connected speech, speed, sentence length, or attention is blocking your English listening comprehension.',
    tldr: 'Reading English and understanding spoken English use overlapping knowledge, but listening also requires fast sound recognition. You may know a word on the page without recognizing its reduced or linked spoken form. Diagnose a few missed sentences with audio and a transcript, then train the specific cause instead of repeatedly playing the whole recording.',
    sections: [
      {
        heading: 'Why written English feels easier',
        body: [
          'Written words remain visible and give you time to reread. Spoken words disappear quickly, change through linking and reduction, and arrive inside a rhythm that may differ from your first language.',
          'This explains why a transcript can look easy after the audio felt impossible. Your vocabulary knowledge may be adequate while your sound-to-word recognition is still slow.'
        ]
      },
      {
        heading: 'Test the real cause with one missed sentence',
        list: [
          'Replay the sentence once without text and say what you understood.',
          'Read the transcript and check whether every word is familiar.',
          'Listen while reading and mark linked, reduced, or weak sounds.',
          'Replay without text and see whether the sentence becomes clear.',
          'Repeat the sentence aloud to connect recognition with production.'
        ]
      },
      {
        heading: 'Common listening problems and fixes',
        table: {
          headers: ['Cause', 'Diagnostic sign', 'Useful practice'],
          rows: [
            ['Vocabulary gap', 'The transcript contains unknown words', 'Learn a few high-value words in context'],
            ['Connected speech', 'The written words are easy but the audio sounds different', 'Loop the phrase and shadow its rhythm'],
            ['Processing speed', 'You understand after pausing', 'Use short chunks, then gradually remove pauses'],
            ['Long sentences', 'You lose the meaning halfway through', 'Listen for meaning groups and summarize each one'],
            ['Unfamiliar accent', 'Specific sounds repeatedly cause errors', 'Use several clear speakers with the same accent']
          ]
        }
      },
      {
        heading: 'Stop replaying the entire audio blindly',
        body: [
          'A full recording may contain only a few sentences that cause most of the difficulty. Identify those lines and work on them directly. Sentence-level replay saves time and gives you clearer evidence of improvement.',
          'After focused practice, return to the whole clip. The final no-text listen checks whether the sentence is genuinely easier to recognize.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner can read a news transcript comfortably but understands only half of the recording. They use GoodScriber to inspect eight missed lines. Five contain familiar words joined together in speech, two are long sentences, and one has new vocabulary. They train each category and then replay the full clip at normal speed.'
        ]
      }
    ],
    faqs: [
      ['Why do I understand the transcript but not the audio?', 'You may know the written words but need more practice recognizing their reduced, linked, and stressed spoken forms.'],
      ['Should I slow down English audio?', 'A small speed reduction can help diagnosis, but return to normal speed after you understand and practice the line.'],
      ['Will watching more English videos solve the problem?', 'Exposure helps, but focused work on missed sentences usually improves specific weaknesses faster.'],
      ['Should I use subtitles when listening?', 'Listen first, use English subtitles to diagnose missed sounds, and then listen again without text.'],
      ['Can speaking practice improve listening?', 'Yes. Repeating a sentence can strengthen your memory of its sound pattern and make it easier to recognize later.']
    ]
  },
  {
    slug: 'how-to-review-ielts-listening-mistakes',
    title: 'How should I review IELTS listening mistakes?',
    description: 'Review IELTS listening mistakes by locating the answer sentence, classifying the cause, studying paraphrases, and replaying the weak section.',
    tldr: 'Do more than check the correct letter or word. For every IELTS listening mistake, locate the answer sentence, compare it with the question, identify the paraphrase, and classify why you missed it. Replay the surrounding audio, write the correct answer under test conditions, and keep an error log that shows recurring causes.',
    sections: [
      {
        heading: 'Start with the answer sentence',
        body: [
          'The useful unit for review is the short section where the answer appears. Find the sentence before the answer, the answer sentence itself, and the sentence after it. This gives enough context to understand signposting and distractors.',
          'Read the question beside the transcript and underline how the recording paraphrases the wording. IELTS often tests meaning through synonyms and reformulation.'
        ]
      },
      {
        heading: 'A complete mistake-review workflow',
        list: [
          'Try the question again before looking at the answer.',
          'Locate the exact answer sentence in the audio and transcript.',
          'Mark the keyword or paraphrase that connects the question to the recording.',
          'Classify the mistake by cause, including spelling and instruction errors.',
          'Replay the section and shadow the key sentence.',
          'Answer the question once more without reading the transcript.',
          'Record the cause in an error log and review recurring categories weekly.'
        ]
      },
      {
        heading: 'Classify IELTS listening errors',
        table: {
          headers: ['Error type', 'Example', 'Review action'],
          rows: [
            ['Sound recognition', 'A familiar word was not heard', 'Loop and shadow the complete phrase'],
            ['Paraphrase', 'The question and audio used different wording', 'Save the synonym pair in context'],
            ['Distractor', 'An early option changed later', 'Mark correction signals such as but or actually'],
            ['Spelling or form', 'The meaning was right but the written answer was invalid', 'Practice exact spelling, singular, plural, and word limits'],
            ['Attention', 'The answer was missed while reading ahead', 'Train question preview and section signposts']
          ]
        }
      },
      {
        heading: 'Build an error log that changes your practice',
        body: [
          'An error log should contain the question type, answer sentence, missed paraphrase, error category, and one next action. Counting wrong answers alone does not show what to train.',
          'When one category repeats, choose the next practice material to target it. Several sound-recognition errors call for sentence looping; repeated spelling errors call for short dictation.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner chooses an incorrect multiple-choice answer because the speaker first mentions one date and later corrects it. They locate both lines in the transcript, mark the correction signal, replay the section, and shadow the final answer sentence. The error log labels it as a distractor rather than a vocabulary problem.'
        ]
      }
    ],
    faqs: [
      ['Should I review every IELTS listening mistake?', 'Yes, but spend the most time on mistakes that reveal a recurring pattern rather than isolated slips.'],
      ['Do I need the transcript for review?', 'A transcript is very useful for locating paraphrases, connected speech, distractors, and exact answer wording.'],
      ['How long should mistake review take?', 'A careful review can take longer than the test section itself. Quality matters more than finishing many practice tests.'],
      ['Should I replay the whole recording?', 'Focus first on the answer area and its surrounding context, then replay the full section once at the end.'],
      ['How can GoodScriber help with IELTS review?', 'You can use timed subtitles to locate answer sentences, replay difficult lines, inspect wording, and shadow key phrases.']
    ]
  },
  {
    slug: 'how-to-practice-english-job-interview-speaking',
    title: 'How can I practice English for a job interview by myself?',
    description: 'Prepare clear English interview answers, turn them into practice audio, shadow useful phrases, record yourself, and answer again without a script.',
    tldr: 'Practice interview ideas and spoken delivery together. Draft short answer points, create a clear model recording, repeat difficult sentences, and then answer the question again without reading. Record several versions and check structure, clarity, pace, and pronunciation. Memorize the message and key phrases, not a rigid paragraph.',
    sections: [
      {
        heading: 'Prepare answer building blocks',
        body: [
          'Start with the questions most likely for your role: introduction, relevant experience, strengths, a difficult situation, and why you want the position. Write bullet points before writing complete sentences.',
          'For experience questions, a simple situation, task, action, and result structure keeps the answer clear. Use facts you can explain naturally rather than adding complicated vocabulary.'
        ]
      },
      {
        heading: 'A self-practice interview routine',
        list: [
          'Choose one interview question and outline three or four answer points.',
          'Write a concise model answer using language you understand and can defend.',
          'Generate or record clear audio for the answer.',
          'Shadow difficult sentences and practice pauses around key ideas.',
          'Hide the script and answer the same question in your own words.',
          'Record the response, review it, and make one targeted improvement.',
          'Repeat the question with slightly different wording.'
        ]
      },
      {
        heading: 'What to check in common answers',
        table: {
          headers: ['Question type', 'Answer focus', 'Speaking check'],
          rows: [
            ['Tell me about yourself', 'Present role, relevant experience, next goal', 'Keep the answer focused and under control'],
            ['Describe a challenge', 'Specific problem, action, measurable result', 'Use clear past tense and sequence words'],
            ['Why this role?', 'Company need and your relevant contribution', 'Sound specific rather than memorized'],
            ['Strengths and weaknesses', 'Evidence and improvement action', 'Avoid long lists and vague claims']
          ]
        }
      },
      {
        heading: 'Do not memorize one fixed paragraph',
        body: [
          'Exact memorization can break when the interviewer changes a question or asks for more detail. Remember the structure, evidence, and useful phrases so you can adapt your answer.',
          'Practice at least two versions of important answers. This builds flexibility and makes your delivery sound more conversational.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A candidate prepares a 75-second answer about solving a customer problem. They add the script to GoodScriber, generate practice audio, and shadow three difficult sentences. Then they close the script, record a new answer from bullet points, and review whether the action and result are clear.'
        ]
      }
    ],
    faqs: [
      ['How many interview questions should I practice?', 'Start with five to eight high-frequency questions for the role, then add technical or industry-specific questions.'],
      ['Should I memorize my English interview answers?', 'Memorize the structure, evidence, and key phrases. Keep enough flexibility to answer follow-up questions naturally.'],
      ['How long should an interview answer be?', 'It depends on the question, but many general answers work well in roughly 45 to 90 seconds.'],
      ['Can text-to-speech help interview practice?', 'Yes. It provides a consistent model for pacing and pronunciation, but your final answer should sound like your own speech.'],
      ['How can I evaluate my recording?', 'Check whether the answer addresses the question, follows a clear structure, uses understandable pronunciation, and avoids unnecessary pauses.']
    ]
  },
  {
    slug: 'how-to-review-phrases-from-listening-materials',
    title: 'How should I review English phrases from listening materials?',
    description: 'Save useful English phrases with their audio context, then use listening recall, shadowing, personal examples, and spaced review to remember them.',
    tldr: 'Keep useful phrases connected to the sentence and audio where you found them. On each review, listen before reading, recall the meaning, shadow the original sentence, and create one personal example. Review a small number after one day, three days, and one week so the phrases move from recognition toward active speaking.',
    sections: [
      {
        heading: 'Save phrases with context',
        body: [
          'A phrase is easier to remember when it remains attached to a speaker, situation, meaning, and sound pattern. Saving an isolated translation removes many of those memory cues.',
          'Keep the original sentence, the short audio line, a simple meaning note, and one reason the phrase is useful to you.'
        ]
      },
      {
        heading: 'A five-step phrase review',
        list: [
          'Listen to the original sentence without reading.',
          'Recall the phrase and explain its meaning in the context.',
          'Read the subtitle and notice pronunciation, stress, and grammar.',
          'Shadow the full sentence two or three times.',
          'Create and say one new sentence about your own life or work.'
        ]
      },
      {
        heading: 'Turn recognition into active use',
        table: {
          headers: ['Review action', 'Skill trained', 'Success check'],
          rows: [
            ['Listen before reading', 'Sound recognition', 'You identify the phrase in real speech'],
            ['Shadow the source sentence', 'Pronunciation and rhythm', 'You can repeat it smoothly'],
            ['Recall the meaning', 'Comprehension', 'You explain it without the translation'],
            ['Make a personal sentence', 'Active speaking', 'You use the phrase in a new context'],
            ['Review after a delay', 'Long-term memory', 'You recall it several days later']
          ]
        }
      },
      {
        heading: 'Use a small spaced-review schedule',
        body: [
          'Review new phrases the next day, about three days later, and again after one week. Phrases that remain difficult can return more often; easy phrases can move to a longer interval.',
          'Limit each session to a manageable group. Five phrases practiced deeply are often more useful than fifty expressions copied without speaking.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner hears the phrase "That makes a lot of sense" in a podcast. They save the complete subtitle line, replay it in GoodScriber, and shadow the speaker. The next day they listen without text and say a personal example: "Taking notes after each lesson makes a lot of sense for me." They review it again three days later.'
        ]
      }
    ],
    faqs: [
      ['How many English phrases should I save each day?', 'Three to five useful phrases are enough when you also listen, repeat, and create personal examples.'],
      ['Should I save single words or complete phrases?', 'Save the complete phrase or sentence when possible because context shows meaning, grammar, pronunciation, and natural combinations.'],
      ['Do I need a translation for every phrase?', 'Use a short translation when it helps, but keep the original sentence and audio as the main review material.'],
      ['How often should I review phrases?', 'A simple schedule is the next day, three days later, and one week later, with extra reviews for difficult items.'],
      ['How can GoodScriber help with phrase review?', 'Timed subtitles and sentence replay let you keep phrases connected to their original audio and practice them aloud in context.']
    ]
  },
  {
    slug: 'how-to-shadow-bbc-6-minute-english',
    updatedAt: '2026-10-07',
    title: 'How do I shadow BBC 6 Minute English?',
    description: 'A step-by-step way to shadow BBC 6 Minute English: work through one episode sentence by sentence with a transcript, record the hard lines, then shadow a full minute without stopping.',
    tldr: 'Take one six-minute episode and stay with it for two or three short sessions. Listen once for the gist, shadow it sentence by sentence with the transcript visible, record the lines you stumble on, and finish by shadowing a one-minute stretch without pausing. Repeating one episode well does more for your speaking than skimming a new one every day.',
    materialLinks: [
      { label: 'BBC 6 Minute English episodes with synced subtitles', path: '/material/bbc6minute' },
      { label: 'All listening materials', path: '/material' }
    ],
    sections: [
      {
        heading: 'Why 6 Minute English suits shadowing',
        body: [
          'BBC 6 Minute English is a BBC Learning English programme in which two presenters discuss one topic for about six minutes and explain a handful of topic words along the way. The speech is clear British English at a moderate, conversational speed.',
          'That combination is useful for shadowing. The episode is short enough to finish, the two voices give you natural turn-taking and intonation to copy, and the vocabulary is explained inside the episode, so you spend less time stopping to look things up.',
          'It is aimed at intermediate learners. If most sentences are too fast to repeat even after several replays, start with slower material and come back later.'
        ]
      },
      {
        heading: 'Step-by-step workflow',
        list: [
          'Pick one episode on a topic you care about and listen to it once without any text, just for the gist.',
          'Open the episode with sentence-by-sentence subtitles and look up the words or phrases you missed.',
          'Shadow line by line: play one sentence, repeat it aloud, and replay it until your stress and pauses are close to the presenter\'s.',
          'Record yourself on the five to ten lines you find hardest and check which words are flagged in the pronunciation feedback.',
          'Shadow a one-minute stretch without pausing, speaking a fraction of a second behind the presenters.',
          'Save two or three useful phrases to your notebook and return to the same episode the next day.'
        ]
      },
      {
        heading: 'A 20-minute session plan',
        table: {
          headers: ['Minutes', 'What to do', 'What you are training'],
          rows: [
            ['0–6', 'Listen to the whole episode once without subtitles', 'Overall comprehension'],
            ['6–14', 'Shadow the first half sentence by sentence with subtitles', 'Sounds, word stress, linking'],
            ['14–18', 'Record the hardest lines and compare with the original', 'Specific problem words'],
            ['18–20', 'Shadow one minute continuously without pausing', 'Rhythm and flow']
          ]
        }
      },
      {
        heading: 'Common mistakes',
        list: [
          'Trying to shadow the whole episode in one go. Work in short stretches and repeat them.',
          'Reading the transcript aloud instead of copying the sound. Look at the text to learn a line, then look away when you repeat it.',
          'Never recording yourself. Without a recording it is hard to notice which words you are changing.',
          'Moving to a new episode every day. Fluency comes from repeating the same lines until they feel easy.'
        ]
      },
      {
        heading: 'Example scenario',
        body: [
          'A learner opens one episode in the GoodScriber listening library. On the first day they listen once, then shadow the first three minutes line by line and record six sentences. On the second day they finish the second half, re-record the same six sentences, and shadow the opening minute without pausing. On the third day they shadow the whole episode once and move on.'
        ]
      }
    ],
    faqs: [
      ['Is BBC 6 Minute English good for shadowing?', 'Yes, for intermediate learners. Episodes are short, the speech is clear and conversational, and the key vocabulary is explained in the episode. Beginners may find the speed difficult and should start with slower material.'],
      ['How long does it take to shadow one episode?', 'Plan two or three sessions of about twenty minutes for one episode. Finishing it in a single sitting usually means rushing instead of repeating.'],
      ['Should I read the transcript while shadowing?', 'Use the transcript while you are learning a line. Once the sound pattern is familiar, repeat it with your eyes off the text so you copy the audio rather than read aloud.'],
      ['Is 6 Minute English British or American English?', 'It is British English. If your goal is an American accent, use it for listening and vocabulary and choose American material for accent shadowing.'],
      ['Where can I shadow 6 Minute English sentence by sentence?', 'The GoodScriber listening library includes BBC 6 Minute English episodes with synced subtitles, sentence replay, recording, and pronunciation practice feedback. You can also upload any audio of your own and practice it the same way.']
    ]
  }
]

export const learnArticles = learnArticleDrafts.map(article => ({
  ...article,
  author: {
    name: 'GoodScriber Editorial Team',
    type: 'Organization',
    url: 'https://www.goodscriber.com/'
  },
  publishedAt: ARTICLE_PUBLISHED_AT[article.slug] || '2026-07-17',
  // 旧文章统一在 2026-07-19 修订过；之后的文章在草稿里自己写 updatedAt
  updatedAt: article.updatedAt || '2026-07-19',
  methodology: 'This guide combines a repeatable GoodScriber product workflow with the external evidence listed below where research is relevant. Example scenarios are illustrative, not user testimonials. Learning results vary by material, proficiency, practice quality, and feedback.',
  sources: (ARTICLE_EVIDENCE[article.slug] || []).map(key => EVIDENCE_SOURCES[key])
}))

export function getLearnArticleBySlug(slug) {
  return learnArticles.find(article => article.slug === slug)
}
